import styled from '@emotion/styled';
import { Text } from 'components/_temp_ui_common/theme/Text';
import { theme } from 'components/core/theme';
import { mediaDesktop } from 'components/core/theme/breakpoints';
import ReactSelect, { GroupBase, InputProps, Props } from 'react-select';

import { InputHelperText } from './Input';

export interface StandardSelectOption {
  label: string;
  value: string;
}

const colors = theme.colors.global;

const customStyles: Props['styles'] = {
  dropdownIndicator: (provided) => ({
    ...provided,
    color: theme.colors.global.greyDark,
  }),
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderColor:
      state.menuIsOpen || state.isFocused
        ? colors.greenDark
        : state.selectProps['aria-invalid'] === 'true'
          ? colors.error
          : colors.lightGreyDark,
    borderRadius: 10,
    borderWidth: 1,
    paddingRight: '8px',

    '&:hover': {
      borderColor: state.menuIsOpen || state.isFocused ? colors.greenDark : '#E7F9ED',
    },
    '&:active': {
      borderColor: colors.greenDark,
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 0 0 8px',
  }),
  singleValue: (provided) => ({
    ...provided,
    position: 'absolute',
    padding: '18px 12px 6px 12px',
    [mediaDesktop]: {
      padding: '19px 12px 7px 12px',
    },
  }),
  indicatorSeparator: () => ({}),
};

const Input = styled.input`
  outline: none;
  width: 100%;
  background-color: rgba(0, 0, 0, 0);

  font-size: 1.125rem;
  padding: 14px 12px;
  ${mediaDesktop} {
    padding: 15px 16px;
    font-size: 1.25rem;
  }

  ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    opacity: 0;
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    opacity: 0;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    opacity: 0;
  }

  &.hasValue + label,
  :focus + label {
    top: 18px;
    transform: translateY(-50%) scale(0.75);
    color: ${colors.greyLight};
  }

  :focus,
  :not(:placeholder-shown) {
    padding: 18px 12px 6px 12px;
  }
  ${mediaDesktop} {
    :focus,
    :not(:placeholder-shown) {
      padding: 19px 12px 7px 12px;
    }
  }

  &.hasValue + label,
  :not(:placeholder-shown) + label {
    top: 18px;
    transform: translateY(-50%) scale(0.75);
  }
`;
const Label = styled.label<{ error?: boolean }>`
  color: ${(props) => (props.error ? colors.error : colors.greyLight)};
  position: absolute;
  font-size: 1rem;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 0 0.3rem;
  margin: 0 0.5rem;
  transition: 0.1s ease-out;
  transform-origin: left top;
  pointer-events: none;
`;

const InputNoLabel = styled.input`
  outline: none;
  width: 100%;
  background-color: rgba(0, 0, 0, 0);

  font-size: 1.125rem;
  padding: 14px 12px;
  ${mediaDesktop} {
    padding: 15px 16px;
    font-size: 1.25rem;
  }
`;

function CustomPlaceholder() {
  return null;
}

function CustomSingleInput<O, I extends boolean, G extends GroupBase<O>>(props: InputProps<O, I, G>) {
  const { cx: _cx, selectProps: inheritedProps, ...rest } = props;
  const selectProps = inheritedProps as SelectProps<O, I, G>;
  return (
    <div>
      <Input
        data-testid={selectProps['data-testid']}
        className={props.hasValue ? 'hasValue' : undefined}
        placeholder={props.selectProps['aria-label']}
        //  themes are technically typescript incompatible, but we're not using them anyway
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(rest as any)}
        error={props.selectProps['aria-invalid'] === 'true'}
        autoComplete="off"
      />
      <Label error={props.selectProps['aria-invalid'] === 'true'}>{props.selectProps['aria-label']}</Label>
    </div>
  );
}

function CustomSingleInputNoLabel<O, I extends boolean, G extends GroupBase<O>>(props: InputProps<O, I, G>) {
  const { cx: _cx, ...rest } = props;
  return (
    <div className="relative">
      <InputNoLabel
        //  themes are technically typescript incompatible, but we're not using them anyway
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        {...(rest as any)}
        error={props.selectProps['aria-invalid'] === 'true'}
        autoComplete="nothing"
      />
    </div>
  );
}

export interface SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends Props<Option, IsMulti, Group> {
  label: string;
  error?: boolean;
  helperText?: string;
  className?: string;
  requiredStar?: string;
  ['data-testid']?: string;
}

function CustomMultiInput<O, I extends boolean, G extends GroupBase<O>>(props: InputProps<O, I, G>) {
  const { cx: _cx, selectProps: inheritedProps, ...rest } = props;
  const selectProps = inheritedProps as SelectProps<O, I, G>;
  const value = (selectProps.value || []) as O[];
  const hasValue = value.length > 0;

  return (
    <div>
      <Input
        data-testid={selectProps['data-testid']}
        className={hasValue ? 'hasValue' : undefined}
        placeholder={selectProps['aria-label']}
        {...(rest as any)} // eslint-disable-line
        error={selectProps['aria-invalid'] === 'true'}
        autoComplete="off"
      />
      <Label error={selectProps['aria-invalid'] === 'true'} style={{ marginBottom: '8px' }}>
        {selectProps['aria-label']}
      </Label>
    </div>
  );
}

export function Select<
  O extends { label: string; value: string },
  I extends boolean = false,
  G extends GroupBase<O> = GroupBase<O>,
>({ error, helperText, label, className, styles, ...props }: SelectProps<O, I, G>) {
  const newStyles = styles ? { ...customStyles, ...styles } : customStyles;
  return (
    <div className={`flex flex-col w-full ${className ?? ''}`}>
      <div className={'relative'}>
        <ReactSelect
          aria-label={label}
          aria-errormessage={`${props.id || props.name}-error-message`}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
          components={{
            Placeholder: CustomPlaceholder,
            Input: props.isMulti ? CustomMultiInput : label ? CustomSingleInput : CustomSingleInputNoLabel,
          }}
          styles={newStyles as Props<O, I, G>['styles']}
          menuPortalTarget={document.body}
          menuPosition={'fixed'}
          formatOptionLabel={({ value, label }) => (
            <Text variant="body-5" data-testid={`select-option-${value}`}>
              {label}
            </Text>
          )}
        />
      </div>
      {error && helperText && (
        <InputHelperText id={`${props.id || props.name}-error-message`} error={error}>
          {helperText}
        </InputHelperText>
      )}
    </div>
  );
}
